/**
 * Tabela estática de valor mínimo por parcela aceito por cada provider
 * de pagamento (gateway) integrado ao Lovable/UaizyChat.
 *
 * Objetivo: bloquear no FRONTEND, antes mesmo da chamada à RPC/Edge Function,
 * a criação ou edição de cobranças cujo valor por parcela esteja abaixo do
 * mínimo aceito pelo provider vinculado à conta corrente selecionada.
 *
 * Espelho idêntico desta tabela existe em SQL (helper
 * `public.get_provider_min_installment_value`) usado pelas RPCs
 * `create_financeiro_cobranca` e `update_financeiro_cobranca`.
 * Ao adicionar/alterar valores aqui, atualize o helper SQL também.
 *
 * Regras gerais:
 *  - Quando o tipo_pagamento for `indefinido` ("Pergunte ao Cliente"), o
 *    cliente final pode acabar escolhendo a forma mais restritiva (boleto),
 *    então adotamos o maior denominador comum do provider (no Asaas: R$ 5,00).
 *  - Providers ausentes da tabela passam sem trava.
 */

// Tipos de pagamento canônicos usados em `financeiro_meios_pagamento.tipo_pagamento`.
export type TipoPagamentoSlug =
  | 'boleto'
  | 'pix'
  | 'cartao_credito'
  | 'cartao_debito'
  | 'dinheiro'
  | 'transferencia'
  | 'indefinido'
  | string;

/**
 * Tabela: provider_slug -> tipo_pagamento -> valor mínimo (em BRL, número).
 * Mantida em sincronia com o helper SQL.
 */
export const PROVIDER_MIN_VALUES: Record<string, Partial<Record<TipoPagamentoSlug, number>>> = {
  asaas: {
    boleto: 5.0,
    pix: 1.0,
    cartao_credito: 3.0,
    cartao_debito: 3.0,
    // "Pergunte ao Cliente" — o cliente pode escolher boleto, então adotamos o maior mínimo.
    indefinido: 5.0,
  },
  // Outros providers (ex.: pagarme, stripe, mercadopago) podem ser adicionados aqui.
};

/** Label amigável do provider para mensagens de erro. */
const PROVIDER_LABELS: Record<string, string> = {
  asaas: 'Asaas',
};

/** Label amigável do tipo de pagamento para mensagens de erro. */
const TIPO_PAGAMENTO_LABELS: Record<string, string> = {
  boleto: 'Boleto',
  pix: 'Pix',
  cartao_credito: 'Cartão de Crédito',
  cartao_debito: 'Cartão de Débito',
  dinheiro: 'Dinheiro',
  transferencia: 'Transferência',
  indefinido: 'Pergunte ao Cliente',
};

export interface ValidateInstallmentMinimumInput {
  /** Slug do provider vinculado à conta corrente (ex.: 'asaas'). */
  providerSlug?: string | null;
  /** Tipo de pagamento canônico do meio de pagamento selecionado. */
  tipoPagamento?: string | null;
  /** Valor total da cobrança. */
  valorTotal: number;
  /** Número de parcelas (use 1 para cobrança avulsa). */
  numeroParcelas: number;
}

export interface ValidateInstallmentMinimumResult {
  /** `true` quando a cobrança passa na trava (ou quando não há regra aplicável). */
  valid: boolean;
  /** Valor mínimo por parcela exigido pelo provider, quando aplicável. */
  minValue?: number;
  /** Valor calculado por parcela (valor_total / numero_parcelas). */
  valorParcela?: number;
  /** Mensagem pronta para exibir ao usuário quando `valid = false`. */
  message?: string;
}

/** Formata número em BRL — uso interno apenas para montar mensagens de erro. */
const formatBRL = (n: number): string =>
  n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/**
 * Valida se o valor por parcela respeita o mínimo do provider+tipo_pagamento.
 * Retorna `valid: true` quando:
 *   - Não há provider informado (cobrança local-only sem gateway);
 *   - O provider não está mapeado na tabela;
 *   - O tipo_pagamento não tem mínimo definido para esse provider;
 *   - O valor por parcela é maior ou igual ao mínimo.
 */
export function validateInstallmentMinimum(
  input: ValidateInstallmentMinimumInput,
): ValidateInstallmentMinimumResult {
  const { providerSlug, tipoPagamento, valorTotal, numeroParcelas } = input;

  // Sem provider → cobrança local-only, sem gateway → não trava.
  if (!providerSlug) return { valid: true };

  const providerRules = PROVIDER_MIN_VALUES[providerSlug];
  if (!providerRules) return { valid: true };

  // Sem tipo_pagamento resolvido → não há como aplicar a regra; libera.
  if (!tipoPagamento) return { valid: true };

  const minValue = providerRules[tipoPagamento as TipoPagamentoSlug];
  if (minValue === undefined || minValue === null) return { valid: true };

  // Defensivo: numero_parcelas inválido cai para 1 (avulsa).
  const parcelas = Number.isFinite(numeroParcelas) && numeroParcelas > 0 ? numeroParcelas : 1;
  const valorParcela = Number((valorTotal / parcelas).toFixed(2));

  if (valorParcela >= minValue) {
    return { valid: true, minValue, valorParcela };
  }

  const providerLabel = PROVIDER_LABELS[providerSlug] ?? providerSlug;
  const tipoLabel = TIPO_PAGAMENTO_LABELS[tipoPagamento] ?? tipoPagamento;

  return {
    valid: false,
    minValue,
    valorParcela,
    message: `O provider ${providerLabel} exige valor mínimo de ${formatBRL(
      minValue,
    )} por parcela para cobranças via ${tipoLabel}. Valor atual por parcela: ${formatBRL(
      valorParcela,
    )}.`,
  };
}
